import React, { useEffect, useState } from "react";
import { editHospital, lookupPincodeDetail } from "../services/hospitalService";

const HOSPITAL_TYPES = [
  { value: "multi_specialty", label: "Multi Specialty" },
  { value: "single_specialty", label: "Single Specialty" },
  { value: "daycare", label: "Day Care" },
  { value: "diagnostic", label: "Diagnostic" },
];

const OT_TYPE_OPTIONS = ["General", "Cardiac", "Neuro", "Orthopedic"];
const ACCREDITATION_OPTIONS = ["NABH", "JCI"];

const SPECIALTIES_OFFERED = [
  { label: "Cardiology & Cardiothoracic Surgery", value: "cardiology_cardiothoracic_surgery" },
  { label: "Neurology & Neurosurgery", value: "neurology_neurosurgery" },
  { label: "Orthopedics & Joint Replacement", value: "orthopedics_joint_replacement" },
  { label: "Gastroenterology & Hepatology", value: "gastroenterology_hepatology" },
  { label: "Oncology & Hemato-Oncology", value: "oncology_hemato_oncology" },
  { label: "Gynecology & Obstetrics", value: "gynecology_obstetrics" },
  { label: "Pediatrics & Neonatology", value: "pediatrics_neonatology" },
  { label: "Nephrology & Urology", value: "nephrology_urology" },
  { label: "Pulmonology & Critical Care", value: "pulmonology_critical_care" },
  { label: "ENT & Head-Neck Surgery", value: "ent_head_neck_surgery" },
  { label: "Ophthalmology", value: "ophthalmology" },
  { label: "Dermatology & Cosmetology", value: "dermatology_cosmetology" },
  { label: "Endocrinology & Diabetology", value: "endocrinology_diabetology" },
  { label: "General Surgery & Laparoscopy", value: "general_surgery_laparoscopy" },
  { label: "Plastic & Reconstructive Surgery", value: "plastic_reconstructive_surgery" },
  { label: "Mental Health & Psychiatry", value: "mental_health_psychiatry" },
  { label: "Emergency Medicine", value: "emergency_medicine" },
  { label: "General Medicine & Family Care", value: "general_medicine_family_care" },
];

const DIAGNOSTIC_SERVICES_OPTIONS = [
  { label: "X-Ray", value: "x_ray" },
  { label: "CT Scan", value: "ct_scan" },
  { label: "MRI", value: "mri" },
  { label: "PET Scan", value: "pet_scan" },
  { label: "Echocardiography", value: "echocardiography" },
  { label: "Ultrasound", value: "ultrasound" },
  { label: "Pathology Labs", value: "pathology_labs" },
];

const AMENITY_OPTIONS = [
  { label: "ATM", value: "atm" },
  { label: "Drinking Water", value: "drinking_water" },
  { label: "AC", value: "ac" },
  { label: "Food Court", value: "food_court" },
  { label: "Washrooms", value: "washrooms" },
  { label: "Lounge", value: "lounge" },
  { label: "Prayer Rooms", value: "prayer_rooms" },
  { label: "Parking", value: "parking" },
  { label: "WiFi", value: "wifi" },
  { label: "Cafeteria", value: "cafeteria" },
  { label: "Ambulance", value: "ambulance" },
];

const inputCls = (field, validationErrors) =>
  `w-full px-3 py-2 text-sm border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    validationErrors[field]
      ? "border-red-400 bg-red-50"
      : "border-gray-300 bg-white hover:border-gray-400"
  }`;

const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
      <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const ChipToggle = ({ selected, onClick, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
      selected
        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
        : "bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600"
    }`}
  >
    {children}
  </button>
);

const ToggleSwitch = ({ name, checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <div className="relative flex-shrink-0">
      <input type="checkbox" name={name} checked={checked} onChange={onChange} className="sr-only" tabIndex={-1} />
      <div className={`w-10 h-5 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-gray-300"}`} />
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </div>
    <span className="text-sm text-gray-700 group-hover:text-gray-900">{label}</span>
  </label>
);

const HospitalFormModal = ({ hospital, currentRole, onClose, onSaved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [pinLookupLoading, setPinLookupLoading] = useState(false);
  const [localities, setLocalities] = useState([]);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    name: hospital.name || "",
    pincode: hospital.pincode || "",
    address_line: hospital.address_line || "",
    city: hospital.city || "",
    state: hospital.state || "",
    sublocality: hospital.sublocality || "",
    hospital_type: hospital.hospital_type || "",
    specialty: hospital.specialty || "",
    accreditations: hospital.accreditations || [],
    no_of_beds: hospital.no_of_bed ?? "",
    no_of_doctors: hospital.no_of_doctor ?? "",
    no_of_ot: hospital.no_of_ot ?? "",
    ot_types: hospital.ot_types || [],
    amenities: hospital.amenities || [],
    diagnostic_services: hospital.diagnostic_services || [],
    specialities_offered: hospital.specialties_offered || [],
    no_of_icus: hospital.no_of_icu ?? "",
    no_of_wards: hospital.no_of_ward ?? "",
    has_pharmacy: !!hospital.has_pharmacy,
    has_radiology: !!hospital.has_radiology,
    has_emergency: !!hospital.has_emergency,
    has_blood_bank: !!hospital.has_blood_bank,
    bus_stand_in_kms: hospital.bus_stand_in_km ?? "",
    railway_in_kms: hospital.railway_in_km ?? "",
    airport_in_kms: hospital.airport_in_km ?? "",
  });

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const onMultiSelectToggle = (name, value) => {
    setFormData((prev) => {
      const current = prev[name] || [];
      return {
        ...prev,
        [name]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      };
    });
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const cleanCityState = (text) => {
    return text.replace(/[0-9]/g, "").trim();
  };

  const lookupPincode = async (pincode, { autofill = true } = {}) => {
    if (!/^\d{6}$/.test(pincode)) return;
    setPinLookupLoading(true);
    try {
      const msg = await lookupPincodeDetail(pincode);
      const locations = msg?.locations || [];
      if (!locations.length) {
        setLocalities([]);
        return;
      }
      // `city` is the locality (post-office) name; `district` is the city.
      const names = [...new Set(locations.map((l) => l.city).filter(Boolean))];
      setLocalities(names);
      const first = locations[0];
      setFormData((prev) => ({
        ...prev,
        city: cleanCityState(first.district),
        state: cleanCityState(first.state),
        // On a fresh pincode entry pick the first locality; when just
        // re-populating the dropdown for an existing record, keep the saved one.
        sublocality:
          autofill || !prev.sublocality ? (names[0] || "") : prev.sublocality,
      }));
    } catch (err) {
      console.error("Pincode lookup failed", err);
    } finally {
      setPinLookupLoading(false);
    }
  };

  // Edit mode: load the full locality list for the saved pincode so the user
  // can pick a different one, but keep the saved sublocality selected.
  useEffect(() => {
    if (formData.pincode) {
      lookupPincode(formData.pincode, { autofill: false });
    } else if (formData.sublocality) {
      setLocalities([formData.sublocality]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim() || formData.name.trim().length > 100)
      errors.name = "Hospital name is required and must be ≤ 100 characters.";
    if (!formData.address_line.trim() || formData.address_line.trim().length > 200)
      errors.address_line = "Full address is required and must be ≤ 200 characters.";
    if (!/^\d{6}$/.test(formData.pincode)) errors.pincode = "PIN code must be 6 digits.";
    if (!formData.state.trim()) {
      errors.state = "State is required.";
    } else if (!/^[a-zA-Z\s\-&.,']+$/.test(formData.state.trim())) {
      errors.state = "State name can only contain letters, spaces, and hyphens.";
    }
    if (!formData.city.trim()) {
      errors.city = "City is required.";
    } else if (!/^[a-zA-Z\s\-&.,']+$/.test(formData.city.trim())) {
      errors.city = "City name can only contain letters, spaces, and hyphens.";
    }
    if (formData.hospital_type === "single_specialty" && !formData.specialty.trim())
      errors.specialty = "Specialty name is required for single specialty hospitals.";

    const optionalRangeCheck = (field, min, max, label) => {
      const val = formData[field];
      if (val === "") return;
      const num = Number(val);
      if (Number.isNaN(num) || num < min || num > max)
        errors[field] = `${label} must be between ${min} and ${max}.`;
    };
    optionalRangeCheck("no_of_beds", 1, 9999, "Number of beds");
    optionalRangeCheck("no_of_doctors", 1, 999, "Number of doctors");
    optionalRangeCheck("no_of_ot", 0, 99, "Number of operation theatres");
    optionalRangeCheck("no_of_icus", 0, 99, "Number of ICUs");
    optionalRangeCheck("no_of_wards", 0, 999, "Number of wards");
    ["bus_stand_in_kms", "railway_in_kms", "airport_in_kms"].forEach((field) => {
      const val = formData[field];
      if (val === "") return;
      const num = Number(val);
      if (Number.isNaN(num) || num < 0) errors[field] = "Distance must be a valid non-negative number.";
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setMessage({ type: "error", text: "Please fix the highlighted errors before submitting." });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const payload = {
        name: formData.name.trim(),
        hospital_type: formData.hospital_type,
        other_hospital_type: null,
        address_line: formData.address_line.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        sublocality: formData.sublocality.trim(),
        pincode: formData.pincode,
        facility_type: formData.hospital_type,
        specialty: formData.hospital_type === "single_specialty" ? formData.specialty.trim() : "",
        accreditations: formData.accreditations,
        no_of_bed: formData.no_of_beds !== "" ? Number(formData.no_of_beds) : null,
        no_of_doctor: formData.no_of_doctors !== "" ? Number(formData.no_of_doctors) : null,
        no_of_ot: formData.no_of_ot !== "" ? Number(formData.no_of_ot) : null,
        ot_types: formData.ot_types,
        no_of_icu: formData.no_of_icus !== "" ? Number(formData.no_of_icus) : null,
        no_of_ward: formData.no_of_wards !== "" ? Number(formData.no_of_wards) : null,
        has_pharmacy: formData.has_pharmacy,
        has_radiology: formData.has_radiology,
        has_emergency: formData.has_emergency,
        has_blood_bank: formData.has_blood_bank,
        bus_stand_in_km: formData.bus_stand_in_kms !== "" ? Number(formData.bus_stand_in_kms) : 0,
        railway_in_km: formData.railway_in_kms !== "" ? Number(formData.railway_in_kms) : 0,
        airport_in_km: formData.airport_in_kms !== "" ? Number(formData.airport_in_kms) : 0,
        amenities: formData.amenities,
        diagnostic_services: formData.diagnostic_services,
        specialties_offered: formData.specialities_offered,
      };
      const res = await editHospital(currentRole, hospital.id, payload);
      const updated = res?.msg || res?.data || res;
      onSaved(updated);
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to update hospital." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-gray-50 rounded-xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[92vh]">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white rounded-t-xl border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Edit Hospital</h2>
            <p className="text-xs text-gray-500 mt-0.5">Update the hospital details below</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none font-light">×</button>
        </div>

        {/* Scrollable form body */}
        <div className="overflow-y-auto flex-1 px-6 py-6">
          <form onSubmit={onSubmit} className="space-y-6" noValidate>

            {/* Basic Information */}
            <SectionCard title="Basic Information" subtitle="Name and classification of the hospital">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Hospital Name <span className="text-red-500 normal-case font-normal">*</span>
                  </label>
                  <input
                    name="name"
                    maxLength={100}
                    value={formData.name}
                    onChange={onInputChange}
                    placeholder="e.g. Apollo Hospitals, Mumbai"
                    className={inputCls("name", validationErrors)}
                  />
                  {validationErrors.name && <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Hospital Type
                  </label>
                  <select
                    name="hospital_type"
                    value={formData.hospital_type}
                    onChange={onInputChange}
                    className={inputCls("hospital_type", validationErrors)}
                  >
                    <option value="">Select type...</option>
                    {HOSPITAL_TYPES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {formData.hospital_type === "single_specialty" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                      Specialty Name <span className="text-red-500 normal-case font-normal">*</span>
                    </label>
                    <input
                      name="specialty"
                      value={formData.specialty}
                      onChange={onInputChange}
                      placeholder="e.g. Cardiology"
                      className={inputCls("specialty", validationErrors)}
                    />
                    {validationErrors.specialty && <p className="text-xs text-red-600 mt-1">{validationErrors.specialty}</p>}
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                    Accreditations
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {ACCREDITATION_OPTIONS.map((acc) => (
                      <ChipToggle
                        key={acc}
                        selected={formData.accreditations.includes(acc)}
                        onClick={() => onMultiSelectToggle("accreditations", acc)}
                      >
                        {acc}
                      </ChipToggle>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Location */}
            <SectionCard title="Location" subtitle="Address and geographic coordinates">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Full Address <span className="text-red-500 normal-case font-normal">*</span>
                  </label>
                  <input
                    name="address_line"
                    maxLength={200}
                    value={formData.address_line}
                    onChange={onInputChange}
                    placeholder="Building, street, area..."
                    className={inputCls("address_line", validationErrors)}
                  />
                  {validationErrors.address_line && <p className="text-xs text-red-600 mt-1">{validationErrors.address_line}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    PIN Code <span className="text-red-500 normal-case font-normal">*</span>
                  </label>
                  <div className="relative">
                    <input
                      name="pincode"
                      value={formData.pincode}
                      placeholder="6-digit PIN"
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d{0,6}$/.test(val)) {
                          onInputChange(e);
                          if (val.length === 6) lookupPincode(val);
                        }
                      }}
                      className={inputCls("pincode", validationErrors)}
                    />
                    {pinLookupLoading && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-500 font-medium">
                        Looking up...
                      </span>
                    )}
                    {!pinLookupLoading && formData.pincode.length === 6 && formData.city && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium">
                        Auto-filled
                      </span>
                    )}
                  </div>
                  {validationErrors.pincode && <p className="text-xs text-red-600 mt-1">{validationErrors.pincode}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Locality
                  </label>
                  <select
                    name="sublocality"
                    value={formData.sublocality}
                    onChange={onInputChange}
                    disabled={pinLookupLoading || localities.length === 0}
                    className={`${inputCls("sublocality", validationErrors)} disabled:bg-gray-50 disabled:text-gray-400`}
                  >
                    <option value="">
                      {pinLookupLoading
                        ? "Loading localities..."
                        : localities.length === 0
                          ? "Enter a PIN code first"
                          : "Select locality"}
                    </option>
                    {/* Preserve a previously-saved locality even if it isn't in the fetched list */}
                    {formData.sublocality &&
                      !localities.includes(formData.sublocality) && (
                        <option value={formData.sublocality}>
                          {formData.sublocality}
                        </option>
                      )}
                    {localities.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    City <span className="text-red-500 normal-case font-normal">*</span>
                  </label>
                  <input
                    name="city"
                    value={formData.city}
                    onChange={onInputChange}
                    placeholder="City"
                    className={inputCls("city", validationErrors)}
                  />
                  {validationErrors.city && <p className="text-xs text-red-600 mt-1">{validationErrors.city}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    State <span className="text-red-500 normal-case font-normal">*</span>
                  </label>
                  <input
                    name="state"
                    value={formData.state}
                    onChange={onInputChange}
                    placeholder="State"
                    className={inputCls("state", validationErrors)}
                  />
                  {validationErrors.state && <p className="text-xs text-red-600 mt-1">{validationErrors.state}</p>}
                </div>
              </div>
            </SectionCard>

            {/* Capacity & Infrastructure */}
            <SectionCard title="Capacity & Infrastructure" subtitle="Operational capacity details">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {[
                  { name: "no_of_beds", label: "No. of Beds", hint: "1–9999" },
                  { name: "no_of_doctors", label: "No. of Doctors", hint: "1–999" },
                  { name: "no_of_ot", label: "Operation Theatres", hint: "0–99" },
                  { name: "no_of_icus", label: "ICUs", hint: "0–99" },
                  { name: "no_of_wards", label: "Wards", hint: "0–999" },
                ].map(({ name, label, hint }) => (
                  <div key={name}>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                      {label}
                      {hint && <span className="text-gray-400 font-normal normal-case ml-1">({hint})</span>}
                    </label>
                    <input
                      type="number"
                      min={0}
                      name={name}
                      value={formData[name]}
                      onChange={onInputChange}
                      className={inputCls(name, validationErrors)}
                    />
                    {validationErrors[name] && <p className="text-xs text-red-600 mt-1">{validationErrors[name]}</p>}
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">OT Types</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {OT_TYPE_OPTIONS.map((ot) => (
                    <ToggleSwitch
                      key={ot}
                      name={ot}
                      checked={formData.ot_types.includes(ot)}
                      onChange={() => onMultiSelectToggle("ot_types", ot)}
                      label={ot}
                    />
                  ))}
                </div>
              </div>
            </SectionCard>

            {/* Services & Facilities */}
            <SectionCard title="Services & Facilities" subtitle="Available services and on-site amenities">
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Core Services</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {[
                      ["has_pharmacy", "Pharmacy"],
                      ["has_radiology", "Radiology"],
                      ["has_emergency", "24x7 Emergency"],
                      ["has_blood_bank", "Blood Bank"],
                    ].map(([name, label]) => (
                      <ToggleSwitch
                        key={name}
                        name={name}
                        checked={Boolean(formData[name])}
                        onChange={onInputChange}
                        label={label}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Amenities</p>
                  <div className="flex gap-2 flex-wrap">
                    {AMENITY_OPTIONS.map(({ label, value }) => (
                      <ChipToggle
                        key={value}
                        selected={formData.amenities.includes(value)}
                        onClick={() => onMultiSelectToggle("amenities", value)}
                      >
                        {label}
                      </ChipToggle>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Diagnostic Services</p>
                  <div className="flex gap-2 flex-wrap">
                    {DIAGNOSTIC_SERVICES_OPTIONS.map(({ label, value }) => (
                      <ChipToggle
                        key={value}
                        selected={formData.diagnostic_services.includes(value)}
                        onClick={() => onMultiSelectToggle("diagnostic_services", value)}
                      >
                        {label}
                      </ChipToggle>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Specialties Offered */}
            <SectionCard title="Specialties Offered" subtitle="Medical specialties available at this hospital">
              <div className="flex gap-2 flex-wrap">
                {SPECIALTIES_OFFERED.map(({ label, value }) => (
                  <ChipToggle
                    key={value}
                    selected={formData.specialities_offered.includes(value)}
                    onClick={() => onMultiSelectToggle("specialities_offered", value)}
                  >
                    {label}
                  </ChipToggle>
                ))}
              </div>
            </SectionCard>

            {/* Nearby Transit */}
            <SectionCard title="Nearby Transit" subtitle="Approximate distances from key transport hubs (in km)">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {[
                  ["bus_stand_in_kms", "Bus Station"],
                  ["railway_in_kms", "Railway Station"],
                  ["airport_in_kms", "Airport"],
                ].map(([name, label]) => (
                  <div key={name}>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                      {label}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        name={name}
                        value={formData[name]}
                        onChange={onInputChange}
                        placeholder="0.0"
                        className={`${inputCls(name, validationErrors)} pr-10`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">
                        km
                      </span>
                    </div>
                    {validationErrors[name] && <p className="text-xs text-red-600 mt-1">{validationErrors[name]}</p>}
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Message */}
            {message && (
              <div
                className={`flex items-start gap-3 px-4 py-3 rounded-lg border text-sm ${
                  message.type === "success"
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {message.type === "success" ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
                {message.text}
              </div>
            )}
          </form>
        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-white rounded-b-xl border-t border-gray-200 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-lg shadow-sm transition-all ${
              submitting ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 active:scale-95"
            }`}
          >
            {submitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HospitalFormModal;
